// layouts/catalogos/grabacion/index.js

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import ListPage from "ui/ListPage";
import ConfirmDialog from "ui/ConfirmDialog";
import SnackbarAlert from "ui/SnackbarAlert";

import { listRecordTypes, deleteRecordType } from "../services/grabacion";
import buildGrabacionTable from "./datosgrabacion";

const toArray = (v) => (Array.isArray(v) ? v : []);

/**
 * Mapa entre nombre de columna (UI) y campo real en BD
 * - "nombre" → name
 * - "creado" → created_at
 */
const SORT_FIELD_MAP = {
  nombre: "name",
  creado: "created_at",
};

// búsqueda simple: texto libre → name[$contains]
function buildSearchParams(input = "") {
  const s = String(input || "").trim();
  if (!s) return {};
  return { "name[$contains]": s };
}

export default function Grabacion() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [pending, setPending] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const openSnack = (m, s = "success") => setSnack({ open: true, message: m, severity: s });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // ordenamiento (igual que en sellos/géneros)
  const [sort, setSort] = useState({
    uiField: "creado",      // nombre de la columna
    apiField: "created_at", // campo real de BD
    direction: "desc",      // "asc" | "desc"
  });

  // fetch con paginación + búsqueda + orden (SERVER-SIDE)
  const fetchList = async ({
    pageIndex = page,
    pageSize = rowsPerPage,
    currentSearch = search,
    sortField = sort.apiField,
    sortDirection = sort.direction,
  } = {}) => {
    setLoading(true);
    setErr("");

    try {
      const limit = pageSize;
      const offset = pageIndex * pageSize;

      const searchParams = buildSearchParams(currentSearch);

      const sortParam =
        sortField != null
          ? { [sortField]: sortDirection === "asc" ? 1 : -1 }
          : null;

      const res = await listRecordTypes({
        __limit: limit,
        __offset: offset,
        ...(sortParam ? { __sort: sortParam } : {}),
        ...searchParams,
      });

      // Igual que en generos/sellos: esperamos { rows, total }
      setItems(res.rows || []);
      setTotal(Number(res.total || 0));
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (columnName) => {
    const apiField = SORT_FIELD_MAP[columnName] || columnName;

    setPage(0); // al cambiar orden, regresar a página 0

    setSort((prev) => {
      let direction = "asc";

      if (prev.uiField === columnName && prev.direction === "asc") {
        direction = "desc";
      }

      const next = {
        uiField: columnName,
        apiField,
        direction,
      };

      // recargar desde backend con nuevo orden
      fetchList({
        pageIndex: 0,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: next.apiField,
        sortDirection: next.direction,
      });

      return next;
    });
  };

  // carga inicial
  useEffect(() => {
    fetchList({
      pageIndex: 0,
      pageSize: rowsPerPage,
      currentSearch: search,
      sortField: sort.apiField,
      sortDirection: sort.direction,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // búsqueda con debounce (server-side)
  useEffect(() => {
    const id = setTimeout(() => {
      setPage(0);
      fetchList({
        pageIndex: 0,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: sort.apiField,
        sortDirection: sort.direction,
      });
    }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, rowsPerPage]);

  const { columns, rows } = useMemo(
    () =>
      buildGrabacionTable(toArray(items), (it) => {
        const id = it.id ?? it._id;
        const nombre = it.name ?? it.nombre ?? "-";
        return {
          id,
          nombre,
          creado: it.created_at ?? it.createdAt,
          onEdit: () => navigate(`/catalogos/grabacion/${id}/editar`),
          onDelete: () => setPending({ id, nombre }),
        };
      }),
    [items, navigate]
  );

  const confirmDelete = async () => {
    if (!pending) return;
    try {
      await deleteRecordType(pending.id);
      openSnack(`Grabación eliminada: ${pending.nombre}`, "success");

      await fetchList({
        pageIndex: page,
        pageSize: rowsPerPage,
        currentSearch: search,
        sortField: sort.apiField,
        sortDirection: sort.direction,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || String(e);
      setErr(`No se pudo eliminar: ${msg}`);
      openSnack(`No se pudo eliminar: ${msg}`, "error");
    } finally {
      setPending(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ListPage
        title="Grabación"
        search={search}
        onSearchChange={setSearch}
        addText="Agregar"
        onAdd={() => navigate("/catalogos/grabacion/nuevo")}
        columns={columns}
        rows={rows}
        loading={loading}
        error={err}
        cardSx={{ maxWidth: "100%", mx: 0 }}
        sortField={sort.uiField}
        sortDirection={sort.direction}
        onSortChange={handleSortChange}
        paginationProps={
          !loading && {
            count: total,
            page,
            rowsPerPage,
            onPageChange: (_e, newPage) => {
              setPage(newPage);
              fetchList({
                pageIndex: newPage,
                pageSize: rowsPerPage,
                currentSearch: search,
                sortField: sort.apiField,
                sortDirection: sort.direction,
              });
            },
            onRowsPerPageChange: (e) => {
              const newSize = parseInt(e.target.value, 10);
              setRowsPerPage(newSize);
              setPage(0);
              fetchList({
                pageIndex: 0,
                pageSize: newSize,
                currentSearch: search,
                sortField: sort.apiField,
                sortDirection: sort.direction,
              });
            },
            rowsPerPageOptions: [5, 10, 25, 50, 100],
            showFirstButton: true,
            showLastButton: true,
          }
        }
      />

      <Footer />

      <ConfirmDialog
        open={!!pending}
        title="Eliminar grabación"
        description={`¿Seguro que quieres eliminar "${pending?.nombre}"?`}
        confirmText="Eliminar"
        onClose={() => setPending(null)}
        onConfirm={confirmDelete}
      />

      <SnackbarAlert
        open={snack.open}
        onClose={closeSnack}
        message={snack.message}
        severity={snack.severity}
      />
    </DashboardLayout>
  );
}
